import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

/**
 * Markdown-visning for assistentsvar: rolig typografi, tabeller med tabulære tall, interne lenker som
 * React Router-lenker (så svarene kan peke rett inn i portalen uten full sideinnlasting).
 */
function isInternal(href) {
  return typeof href === 'string' && href.startsWith('/') && !href.startsWith('//');
}

/* Seksjonsoverskrifter i svarene: liten etikett med fargemarkør, slik at observasjon, hypotese og neste steg leses raskt. */
const SECTION_TONE = [
  [/observasjon|hva tallene viser|nøkkeltall/i, 'bg-[var(--hr-navy)]'],
  [/forklaring|hypotese|årsak/i, 'bg-[var(--hr-amber)]'],
  [/neste steg|anbefal|tiltak/i, 'bg-[var(--hr-green)]'],
  [/usikker|mangler|forbehold|datakvalitet/i, 'bg-[var(--hr-red)]'],
];
function SectionHeading({ children }) {
  const text = React.Children.toArray(children).join('');
  const tone = SECTION_TONE.find(([re]) => re.test(text))?.[1] ?? 'bg-[var(--hr-muted-2)]';
  return (
    <h4 className="mb-2 mt-6 flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted)] first:mt-0">
      <span className={cn('h-1.5 w-1.5 rounded-full', tone)} aria-hidden="true" />
      {children}
    </h4>
  );
}

const components = {
  h1: ({ children }) => <h3 className="mt-5 font-display text-[18px] font-semibold leading-snug text-[var(--hr-heading)] first:mt-0">{children}</h3>,
  h2: SectionHeading,
  h3: SectionHeading,
  h4: ({ children }) => <h5 className="mt-3 text-[14px] font-semibold text-[var(--hr-heading)]">{children}</h5>,
  p: ({ children }) => <p className="my-2.5 leading-7 first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="my-2.5 list-disc space-y-1 pl-5 marker:text-[var(--hr-muted-2)]">{children}</ul>,
  ol: ({ children }) => <ol className="my-2.5 list-decimal space-y-1 pl-5 marker:text-[var(--hr-muted)]">{children}</ol>,
  li: ({ children }) => <li className="leading-7 [&>p]:my-0">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-[var(--hr-heading)]">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  hr: () => <hr className="my-4 border-[var(--hr-hairline)]" />,
  blockquote: ({ children }) => <blockquote className="my-3 border-l-2 border-[var(--hr-accent)] pl-4 text-[var(--hr-muted)] [&>p]:my-1">{children}</blockquote>,
  a: ({ href, children }) =>
    isInternal(href) ? (
      <Link to={href} className="font-medium text-[var(--hr-accent-text)] underline decoration-[var(--hr-accent)]/40 underline-offset-[3px] transition-colors duration-150 hover:decoration-[var(--hr-accent)]" data-testid="assistant-internal-link">
        {children}
      </Link>
    ) : (
      <a href={href} target="_blank" rel="noreferrer" className="font-medium text-[var(--hr-accent-text)] underline decoration-[var(--hr-accent)]/40 underline-offset-[3px] hover:decoration-[var(--hr-accent)]">
        {children}
      </a>
    ),
  code: ({ inline, className, children }) => {
    const text = String(children ?? '');
    const block = !inline && (className || text.includes('\n'));
    if (!block) return <code className="rounded-[5px] bg-[var(--hr-inset)] px-1.5 py-0.5 font-mono text-[13px] text-[var(--hr-heading)]">{text}</code>;
    return (
      <pre className="hr-scroll my-3 overflow-x-auto rounded-[10px] border border-[var(--hr-hairline)] bg-[var(--hr-surface-2)] p-3.5 font-mono text-[13px] leading-6 text-[var(--hr-heading)]">
        <code>{text.replace(/\n$/, '')}</code>
      </pre>
    );
  },
  pre: ({ children }) => <>{children}</>,
  table: ({ children }) => (
    <div className="hr-scroll my-3 overflow-x-auto rounded-[12px] border border-[var(--hr-hairline)] shadow-[var(--hr-shadow-xs)]">
      <table className="w-full border-collapse text-[14px]">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-[var(--hr-surface-2)] text-left text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--hr-muted)]">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-[var(--hr-hairline)]">{children}</tbody>,
  tr: ({ children }) => <tr className="[&:nth-child(even)]:bg-[rgba(31,33,48,0.015)]">{children}</tr>,
  th: ({ children, style }) => (
    <th className={cn('px-3 py-2 font-semibold', style?.textAlign === 'right' && 'text-right')} style={style}>
      {children}
    </th>
  ),
  td: ({ children, style }) => {
    const text = React.Children.toArray(children).join('');
    const numeric = /^[\s\d.,\u00a0%+−-]+(\s?[a-zA-Z₂/%]+)?$/.test(text) && /\d/.test(text);
    return (
      <td className={cn('px-3 py-2 align-top text-[var(--hr-text)]', (numeric || style?.textAlign === 'right') && 'hr-tabular text-right')} style={style}>
        {children}
      </td>
    );
  },
};

/*
  Modellen skriver av og til portalstier som ren tekst («se /maskiner?scenario=…»). Vi gjør dem klikkbare
  før rendring – men bare stier som ikke allerede står i en markdown-lenke, kodeblokk eller URL.
*/
const PORTAL_PATH = /(^|[\s(«"'*_])(\/(?:oversikt|produksjon|maskiner|sammenlign|avvik|rapporter|datakilder|bestillinger|assistent)(?:\/[A-Za-z0-9-]+)?(?:\?[A-Za-z0-9=&%._-]+)?)(?=$|[\s)»"'*_.,;:!?])/g;
const LABELS = { oversikt: 'Oversikt', produksjon: 'Produksjon', maskiner: 'Maskiner', sammenlign: 'Sammenlign', avvik: 'Avvik', rapporter: 'Rapporter', datakilder: 'Datakilder', bestillinger: 'Bestillinger', assistent: 'KI-assistent' };

export function autolinkPortalPaths(md) {
  if (!md) return md;
  // Hopp over kodeblokker og eksisterende lenker/URL-er ved å dele teksten i segmenter som ikke skal røres.
  const parts = md.split(/(```[\s\S]*?```|`[^`\n]*`|\]\([^)]*\)|https?:\/\/\S+)/g);
  return parts
    .map((seg, i) => {
      if (i % 2 === 1) return seg; // beskyttet segment
      return seg.replace(PORTAL_PATH, (m, pre, path) => {
        const key = path.split(/[/?]/)[1];
        const detail = path.match(/^\/[a-z]+\/([A-Za-z0-9-]+)/);
        const label = detail ? `${LABELS[key] ?? key} · ${detail[1]}` : LABELS[key] ?? key;
        return `${pre}[${label}](${path})`;
      });
    })
    .join('');
}

export function Markdown({ children, className }) {
  const text = typeof children === 'string' ? autolinkPortalPaths(children) : children;
  return (
    <div className={cn('text-[16px] text-[var(--hr-text)]', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
}
